import { signal } from '@preact/signals-react';
import CalendarFilter from 'CalendarFilter';
import LoadingCalendar from 'components/Loading';
import Text from 'components/Text';
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import styled from 'styled-components';
import CalendarViewBody from './Body';
import CalendarViewHeader from './Header';
import { getNearestTimeSlotId } from './helper';
import CalendarStyled, { calendarDOM, isModeFull } from './styles';
import { ICalendarSchema } from './types';
import useCalendarFilter from './useCalendarFilter';
import useCalendarMapping, { ICalendarMappingResultValues } from './useCalendarMapping';
import shopSelectors from 'services/selectors/shop';
import clsx from 'clsx';

export const calendarScrollTo = signal<(to: number, left?: number) => void>(
  () => undefined
);
// function easeInOutQuint(t: number) {
//   return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
// }
const Calendar = () => {
  const enablePaging = shopSelectors.enableCalendarPaging();
  const calendarRef = useRef<HTMLTableElement>(null);
  const schema = useCalendarFilter();
  const body = useCalendarMapping(schema);

  useLayoutEffect(() => {
    calendarScrollTo.value = (top: number, left?: number) => {
      setTimeout(
        () =>
          calendarRef.current?.scrollTo({ top: top, left, behavior: 'smooth' }),
        0
      );
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const id = getNearestTimeSlotId();
      const element = document.getElementById(id);
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      });
    }, 1000);
  }, []);

  return (
    <>
      <CalendarFilter enablePaging={enablePaging} />
      <Text className='spacing-div' mb={isModeFull.value ? 0 : 1.5} />
      <CalendarTableViewStyled
        id='calendar'
        ref={calendarDOM}
        className={'zoom-level-1'}
        colLength={schema.headers.length}
      >
        <ObserverCalendar enablePaging={enablePaging} calendarRef={calendarRef} schema={schema} body={body} />
        {/* <CalendarTableOverflowStyled
          ref={calendarRef}
          className='drag-scroll-container'
          id='drag-scroll-container'
        >
          <div className='calendar'>
            <CalendarStyled.Table
              typeView={schema.typeView}
              style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}
            >
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: paddingTop }} colSpan={999} />
                </tr>
              )}
              <CalendarViewHeader
                headers={schema.headers}
                renderHeader={schema.renderHeader}
              />
              <CalendarViewBody
                rows={rows}
                rowsSpanInfo={body.rowsSpanInfo}
                distanceTimeline={schema.distanceTimeline}
              />
              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: paddingBottom }} colSpan={999} />
                </tr>
              )}
            </CalendarStyled.Table>
          </div>
        </CalendarTableOverflowStyled> */}
        <LoadingCalendar />
      </CalendarTableViewStyled>
    </>
  );
};

export default memo(Calendar);

const ObserverCalendar = memo(({ enablePaging, calendarRef, schema, body }: { enablePaging: boolean, calendarRef: React.RefObject<HTMLDivElement>, schema: ICalendarSchema, body: ICalendarMappingResultValues }) => {
  // const scrollingRef = React.useRef<number>();

  // const scrollToFn: VirtualizerOptions<any, any>['scrollToFn'] =
  //   React.useCallback((offset, canSmooth, instance) => {
  //     const duration = 1000;
  //     const start = calendarRef.current?.scrollTop || 0;
  //     const startTime = (scrollingRef.current = Date.now());

  //     const run = () => {
  //       if (scrollingRef.current !== startTime) return;
  //       const now = Date.now();
  //       const elapsed = now - startTime;
  //       const progress = easeInOutQuint(Math.min(elapsed / duration, 1));
  //       const interpolated = start + (offset - start) * progress;

  //       if (elapsed < duration) {
  //         elementScroll(interpolated, canSmooth, instance);
  //         requestAnimationFrame(run);
  //       } else {
  //         elementScroll(interpolated, canSmooth, instance);
  //       }
  //     };

  //     requestAnimationFrame(run);
  //   }, []);

  // const rowVirtualizer = useVirtualizer({
  //   count: body.rows.length,
  //   getScrollElement: () => calendarRef.current,
  //   estimateSize: () => 52,
  //   overscan: 5,
  //   scrollToFn,
  // });

  // const virtualItems = rowVirtualizer.getVirtualItems();
  // const totalSize = rowVirtualizer.getTotalSize();

  // const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  // const paddingBottom =
  //   virtualItems.length > 0
  //     ? totalSize - virtualItems[virtualItems.length - 1].end
  //     : 0;

  // const rows = useMemo(
  //   () => virtualItems.map((item) => body.rows[item.index]),
  //   [body.rows, virtualItems]
  // );

  return (
    <CalendarTableOverflowStyled
      ref={calendarRef}
      className={clsx('drag-scroll-container', !enablePaging && 'non-enable-paging')}
      id='drag-scroll-container'
    // style={{ height: `${totalSize}px` }}
    >
      <div className='calendar'>
        <CalendarStyled.Table
          typeView={schema.typeView}
        >
          {/* {paddingTop > 0 && (
            <tr>
              <td style={{ height: paddingTop }} colSpan={999} />
            </tr>
          )} */}
          <CalendarViewHeader
            headers={schema.headers}
            renderHeader={schema.renderHeader}
            fullScreen={schema.fullScreen}
            enablePaging={enablePaging}
          />
          <CalendarViewBody
            rows={body.rows}
            headers={schema.headers}
            rowsSpanInfo={body.rowsSpanInfo}
            distanceTimeline={schema.distanceTimeline}
            fullScreen={schema.fullScreen}
            enablePaging={enablePaging}
          />
          {/* {paddingBottom > 0 && (
            <tr>
              <td style={{ height: paddingBottom }} colSpan={999} />
            </tr>
          )} */}
        </CalendarStyled.Table>
      </div>
    </CalendarTableOverflowStyled>
  );
});

const CalendarTableViewStyled = styled.div<{ colLength?: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-grow: 1;
  flex-shrink: 0;

  &.zoom-level-1 {
    .zoom-control-header {
      zoom: 1.2;
    }
    .zoom-control-body {
      zoom: 0.8;
    }
    .calendar table thead th {
      height: 35px;
      min-width: 10rem;
      max-width: 10rem;
      .ant-avatar.ant-avatar-lg {
        width: 30px;
        height: 30px;
      }
      .StaffBox .subtext {
        font-size: var(--zoom-level-1-subtext) !important;
      }
    }
    .calendar table tbody td {
      height: 60px;
      min-width: 10rem;
      max-width: 10rem;

      .text-time {
        font-size: var(--zoom-level-1-text);
      }

      .AppointmentStyled,
      .MultiAppointmentStyled,
      .BlockHourItemStyled {
        .appointment-service-sm,
        .appointment-time-sm,
        .appointment-name-sm {
          font-size: var(--zoom-level-1-appointment-sm);
          line-height: calc(var(--zoom-level-1-appointment-sm) + 4px);
        }
        .appointment-icon-sm {
          width: 20px;
          padding: 0;
        }
        .appointment-icon {
          width: 20px;
          padding: 0;
          display: flex;
        }

        .appointment-time,
        .appointment-name,
        .appointment-service {
          font-size: var(--zoom-level-1-text);
        }
        .block-hour-time {
          font-size: var(--zoom-level-1-text);
        }
        .block-hour-title {
          font-size: calc(var(--zoom-level-1-text) - 2px);
        }
        .block-hour-note {
          font-size: calc(var(--zoom-level-1-text) - 2px);
        }
      }
    }
  }

  &.zoom-level-2 {
    .zoom-control-body {
      zoom: 0.6;
    }
    .calendar table thead th {
      height: 30px;
      min-width: 7rem;
      max-width: 7rem;
      .ant-avatar.ant-avatar-lg {
        width: 25px;
        height: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        .ant-avatar-string {
          position: relative;
          transform: unset !important;
          left: unset;
        }
      }
      .StaffBox .subtext {
        font-size: calc(var(--zoom-level-2-subtext) + 20px) !important;
        margin-top: 0 !important;
      }
    }
    .calendar table .fixed-time-td {
      max-width: 6.5rem !important;
      min-width: 6.5rem !important;
      width: 6.5rem !important;
    }
    .calendar table .fixed-time-anyone {
      left: 6.5rem;
    }
    .calendar table tbody td {
      height: 50px;
      min-width: 7rem;
      max-width: 7rem;

      .text-time {
        font-size: var(--zoom-level-2-text);
      }

      .AppointmentStyled,
      .MultiAppointmentStyled,
      .BlockHourItemStyled {
        .appointment-service-sm,
        .appointment-time-sm,
        .appointment-name-sm {
          font-size: var(--zoom-level-2-appointment-sm);
          line-height: calc(var(--zoom-level-2-appointment-sm) + 4px);
        }
        .appointment-icon-sm {
          width: 12px;
          height: 12px;
          display: flex;
          padding: 0;
          svg {
            height: 100%;
            width: 100%;
          }
        }
        .appointment-icon {
          width: 16px;
          height: 16px;
          display: flex;
          padding: 0;
          svg {
            height: 100%;
            width: 100%;
          }
        }
        .appointment-box.appointment-box-sm {
          justify-content: unset;
        }
        .appointment-time,
        .appointment-name {
          font-size: var(--zoom-level-2-text);
        }
        .appointment-alert-icon svg {
          transform: scale(1);
        }
        .appointment-service {
          font-size: calc(var(--zoom-level-2-text) - 2px);
        }
        .block-hour-time {
          font-size: calc(var(--zoom-level-2-text) - 2px);
        }
        .appointment-block-icon {
          width: 12px;
          height: 12px;
          display: flex;
          padding: 0;
          svg {
            height: 100%;
            width: 100%;
          }
        }
        .block-hour-title {
          font-size: calc(var(--zoom-level-2-text) - 2px);
        }
        .block-hour-note {
          font-size: calc(var(--zoom-level-2-text) - 2px);
        }
      }
    }
    .drag-scroll-container.non-enable-paging {
      .calendar table .fixed-time-td {
        max-width: 6.5rem !important;
        min-width: 6.5rem !important;
        width: 6.5rem !important;
      }
      .calendar table .fixed-time-anyone {
        left: 6.5rem;
        min-width: unset;
        max-width: unset;
        width: ${({ colLength }) => colLength ? `${100 / colLength}%` : 'auto'} !important;
      }
      .calendar table tbody td {
        height: 50px;
        min-width: unset;
        max-width: unset;
        width: ${({ colLength }) => colLength ? `${100 / colLength}%` : 'auto'} !important;
      }
    }
  }

  &.zoom-level-0 {
    .zoom-control-header {
      zoom: 1.2;
    }
    .zoom-control-body {
      zoom: 0.9;
    }
    .calendar table thead th {
      height: 40px;
      min-width: 12rem;
      max-width: 12rem;
      .ant-avatar.ant-avatar-lg {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        .ant-avatar-string {
          position: relative;
          transform: unset !important;
          left: unset;
        }
      }
      .StaffBox .subtext {
        font-size: var(--zoom-level-0-subtext) !important;
      }
    }
    .calendar table .fixed-time-td {
      max-width: 7.5rem !important;
      min-width: 7.5rem !important;
      width: 7.5rem !important;
    }
    .calendar table .fixed-time-anyone {
      left: 7.5rem;
    }
    .calendar table tbody td {
      height: 60px;
      min-width: 12rem;
      max-width: 12rem;

      .text-time {
        font-size: var(--zoom-level-0-text);
      }

      .AppointmentStyled,
      .MultiAppointmentStyled,
      .BlockHourItemStyled {
        .appointment-icon-sm {
          width: 16px;
          height: 16px;
          display: flex;
          padding: 0;
          svg {
            height: 100%;
            width: 100%;
          }
        }
        .appointment-icon {
          width: 16px;
          height: 16px;
          display: flex;
          padding: 0;
          svg {
            height: 100%;
            width: 100%;
          }
        }
        .appointment-box.appointment-box-sm {
          justify-content: unset;
        }
        .appointment-time,
        .appointment-name,
        .appointment-service {
          font-size: var(--zoom-level-0-text);
        }
        .appointment-alert-icon svg {
          transform: scale(1.3);
        }
        .block-hour-time {
          font-size: var(--zoom-level-0-text);
        }
        .appointment-block-icon {
          width: 16px;
          height: 16px;
          display: flex;
          padding: 0;
          svg {
            height: 100%;
            width: 100%;
          }
        }
        .block-hour-title {
          font-size: calc(var(--zoom-level-0-text) - 2px);
        }
        .block-hour-note {
          font-size: calc(var(--zoom-level-0-text) - 2px);
        }
      }
    }
  }
`;

const CalendarTableOverflowStyled = styled.div`
  position: relative;
  max-height: 100%;
  flex: 1;
  height: 100%;
  z-index: 1;
  max-width: 100%;
  overflow: scroll;
  overscroll-behavior: none;

  .calendar {
    position: absolute;
    inset: 0;
  }
`;
